import { useMemo } from 'react';
import { label } from '@entities/ksp/format';
import type { KspAction, KspWorkspace } from '@entities/ksp/types';
import type { FormField } from '@features/ksp-actions/ActionForm';
import { Button } from '@shared/ui/Button';
import { Card, Muted, Small, Stack, TwoColumns } from '../styles';
import { choice, dateField, textField } from '../types';
import { AttentionCard } from '../overview/AttentionCard';
import { MilestoneStrip } from '../overview/MilestoneStrip';
import { PlanFactCurve } from '../overview/PlanFactCurve';
import { PulseTiles } from '../overview/PulseTiles';
import { progress, type PulseInput } from '../overview/pulse';

export interface OverviewTabProps {
  workspace: KspWorkspace;
  projectId: string;
  versionId?: string | null;
  timezone: string;
  params: URLSearchParams;
  permission: (action?: KspAction) => boolean;
  open: (
    title: string,
    fields: FormField[],
    action: (v: Record<string, string>) => Promise<unknown>,
    submit?: string,
  ) => void;
  execute: (
    path: string,
    body: Record<string, unknown>,
    method?: 'POST' | 'PATCH' | 'DELETE',
  ) => Promise<unknown>;
  proposal: {
    id: string;
    action: string;
    text: string;
    refs: string[];
  } | null;
  setProposal: (
    proposal: {
      id: string;
      action: string;
      text: string;
      refs: string[];
    } | null,
  ) => void;
}

/**
 * «Сводка» — первый экран проекта: успеваем ли к договорному сроку, сколько сделано,
 * ближайшая веха, факт за неделю; кривая план-факт, что требует внимания, лента вех.
 */
export function OverviewTab({
  workspace: d,
  projectId,
  versionId,
  timezone,
  params,
  permission,
  open,
  execute,
  proposal,
  setProposal,
}: OverviewTabProps) {
  const now = useMemo(() => new Date(), []);
  const input: PulseInput = {
    summary: d.summary,
    milestones: d.milestones,
    activities: d.activities,
    actual_submissions: d.actual_submissions,
    operational: d.operational,
    source_health: d.source_health,
  };
  return (
    <>
      <PulseTiles input={input} timezone={timezone} now={now} />
      <TwoColumns>
        <Stack>
          <PlanFactCurve
            curves={d.summary?.curves}
            contractFinish={d.summary?.contract_finish ?? null}
            timezone={timezone}
            reason={progress(d.summary, label).reason}
          />
          <MilestoneStrip input={input} timezone={timezone} now={now} />
        </Stack>
        <AttentionCard input={input} params={params} now={now} />
      </TwoColumns>
      <Card>
        <h3>Помощник инженера КСП</h3>
        <Muted>
          Задайте вопрос и опишите подтверждённые обстоятельства — помощник подготовит пояснение
          или проект месячно-суточного задания. Решение принимает инженер: предложение
          сохраняется черновиком и применяется только после проверки.
        </Muted>
        <Button
          disabled={!permission() || !d.summary}
          onClick={() =>
            open(
              'Подготовить предложение инженеру',
              [
                {
                  ...textField('question', 'Вопрос и подтверждённые обстоятельства'),
                  type: 'textarea',
                },
                choice(
                  'action',
                  'Результат',
                  [
                    { value: 'explanation', label: 'Пояснение и вопросы для проверки' },
                    {
                      value: 'period.create',
                      label: 'Предложение месячно-суточного задания',
                    },
                  ],
                  'explanation',
                ),
                {
                  ...dateField('start_date', 'Начало предлагаемого периода'),
                  required: false,
                },
                {
                  ...dateField('end_date_exclusive', 'Конец периода (не включается)'),
                  required: false,
                },
                choice(
                  'quantity_precision',
                  'Точность количества',
                  [
                    { value: '2', label: '0.01' },
                    { value: '3', label: '0.001' },
                    { value: '8', label: '0.00000001' },
                  ],
                  '8',
                ),
              ],
              async (values) => {
                const saved = (await execute('assistant/proposals', {
                  project_id: projectId,
                  question: values.question,
                  action: values.action,
                  command: {
                    start_date: values.start_date,
                    end_date_exclusive: values.end_date_exclusive,
                    version_id: versionId,
                    quantity_precision: Number(values.quantity_precision),
                  },
                })) as
                  | {
                      id?: string;
                      payload?: {
                        _proposal_context?: { explanation?: string; source_refs?: string[] };
                      };
                    }
                  | undefined;
                const context = saved?.payload?._proposal_context;
                if (typeof saved?.id === 'string')
                  setProposal({
                    id: saved.id,
                    action: values.action,
                    text:
                      context?.explanation ??
                      'Предложение сохранено. Перед применением проверьте исходные данные.',
                    refs: context?.source_refs ?? [],
                  });
              },
            )
          }
        >
          Подготовить предложение
        </Button>
        {!d.summary && (
          <Small>Помощник работает по опубликованному расчёту — сначала рассчитайте график.</Small>
        )}
        {proposal && (
          <Card>
            <Muted>{proposal.text}</Muted>
            {proposal.refs.length > 0 && (
              <Small title={proposal.refs.join('\n')}>
                Источников в обосновании: {proposal.refs.length}
              </Small>
            )}
            {proposal.action !== 'explanation' && (
              <Button
                disabled={!permission()}
                onClick={() =>
                  open(
                    'Применить проверенное предложение',
                    [textField('basis', 'Решение ответственного инженера')],
                    (values) => execute(`proposals/${proposal.id}/apply`, values),
                  )
                }
              >
                Применить проверенное предложение
              </Button>
            )}
          </Card>
        )}
      </Card>
    </>
  );
}
